import { FC, useEffect, useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Loader, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Surveys } from '@/_shared/components/Surveys';
import {
  getSurveyCampaignByIdResponsesResultsStatsCall,
  getSurveyCampaignsResultsStatsCall,
  getTemplatesCall,
} from '@/surveys/state';
import { EmptyState } from '../EmptyState';
import { DepartmentDistributionBox } from './DepartmentDistributionBox';
import { RecapDepartmentResults } from './RecapDepartmentResults';
import { SurveyDepartmentResultsVsOrganization } from './SurveyDepartmentResultsVsOrganization';

type Props = {
  departmentId?: string;
  departmentName?: string;
  activeChip: string;
};

export const AssessmentByTemplate: FC<Props> = ({ departmentId, departmentName, activeChip }) => {
  const t = useTranslations('departments.tabs.department.assessment');
  const tShared = useTranslations();

  const {
    execute: executeGetSurveyCampaignByIdResponsesResultsStats,
    loading: loadingGetSurveyCampaignByIdResponsesResultsStats,
    error: errorGetSurveyCampaignByIdResponsesResultsStats,
    success: successGetSurveyCampaignByIdResponsesResultsStats,
    departmentsStats,
  } = getSurveyCampaignByIdResponsesResultsStatsCall.hook();

  const { response: responseGetSurveyCampaignsResultsStats } =
    getSurveyCampaignsResultsStatsCall.hook();

  const { response: responseGetTemplates } = getTemplatesCall.hook();

  const currentTemplateId = useMemo(() => {
    const templateId = responseGetTemplates?.find(template => template.name === activeChip)?.id;
    return templateId;
  }, [activeChip, responseGetTemplates]);

  const currentCampaignId = useMemo(() => {
    const campaign = responseGetSurveyCampaignsResultsStats?.findLast(
      currentCampaign => currentCampaign.surveyTemplateId === currentTemplateId,
    );
    return campaign?.campaignId;
  }, [currentTemplateId, responseGetSurveyCampaignsResultsStats]);

  const nameSurvey = useMemo(() => {
    if (responseGetTemplates && responseGetSurveyCampaignsResultsStats) {
      const templateName = responseGetTemplates.find(
        template => template?.id === currentTemplateId,
      );
      return templateName?.name || '';
    }
    return '';
  }, [responseGetTemplates, responseGetSurveyCampaignsResultsStats, currentTemplateId]);

  const totalCompletedSurveys = useMemo(() => {
    return departmentsStats?.[0]?.totalCompletedSurveys || 0;
  }, [departmentsStats]);

  const totalInvited = useMemo(() => {
    return departmentsStats?.[0]?.totalInvited || 0;
  }, [departmentsStats]);

  const notInvited =
    departmentsStats?.[0].totalCompletedSurveys === 0 && departmentsStats?.[0].totalInvited === 0;

  useEffect(() => {
    if (departmentId && currentCampaignId) {
      executeGetSurveyCampaignByIdResponsesResultsStats({
        campaignId: currentCampaignId,
        departmentId,
      });
    }
  }, [currentCampaignId, departmentId, executeGetSurveyCampaignByIdResponsesResultsStats]);

  if (loadingGetSurveyCampaignByIdResponsesResultsStats) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorGetSurveyCampaignByIdResponsesResultsStats) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (currentCampaignId === undefined) {
    return <EmptyState />;
  }

  if (
    successGetSurveyCampaignByIdResponsesResultsStats &&
    !loadingGetSurveyCampaignByIdResponsesResultsStats &&
    !errorGetSurveyCampaignByIdResponsesResultsStats
  ) {
    return (
      <Box>
        {departmentsStats?.length === 0 || notInvited ? (
          <EmptyState />
        ) : (
          <Box mt='m'>
            <Box>
              {totalCompletedSurveys > 0 && (
                <>
                  <Typography variant='h3' mt='xxs' mb='xxs'>
                    {nameSurvey}
                  </Typography>
                  <Typography variant='p3/light' mb='m'>
                    {t(
                      totalCompletedSurveys === 1
                        ? 'intro.description.single'
                        : 'intro.description.plural',
                      {
                        departmentName,
                        nameSurvey,
                        totalCompletedSurveys,
                      },
                    )}
                  </Typography>
                </>
              )}
              <Surveys.RecapAssessment
                invitedTotal={totalInvited || 0}
                completedTotal={totalCompletedSurveys || 0}
                linkAssessment={currentTemplateId}
                templateName={nameSurvey}
              />
              {totalCompletedSurveys > 0 && (
                <>
                  <RecapDepartmentResults currentTemplateId={currentTemplateId} />
                  <Box mt='l'>
                    <SurveyDepartmentResultsVsOrganization />
                  </Box>
                  <Box mt='l'>
                    <DepartmentDistributionBox />
                  </Box>
                </>
              )}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  return null;
};
